import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Sparkles, Palette, Type, MessageSquare, 
  Layout, Loader2, CheckCircle, Copy, RefreshCw, Upload, Image, Trash2
} from "lucide-react";

// Color swatch component (defined outside to avoid re-renders)
const ColorSwatch = ({ color }) => (
  <div className="flex items-center space-x-2 p-2 bg-white rounded border">
    <div 
      className="w-8 h-8 rounded shadow-sm border"
      style={{ backgroundColor: color.hex }}
    />
    <div>
      <p className="text-sm font-medium">{color.name}</p>
      <p className="text-xs text-neutral-500">{color.hex}</p>
    </div>
  </div>
);

export const BrandGuidelinesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [guidelines, setGuidelines] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [generatingLogo, setGeneratingLogo] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    business_name: "",
    industry: "",
    target_audience: "",
    brand_personality: "",
    core_values: "",
    unique_selling_proposition: "",
    competitors: "",
    preferred_colors: "",
    existing_tagline: ""
  });

  useEffect(() => {
    fetchGuidelines();
  }, []);

  const fetchGuidelines = async () => {
    try {
      const response = await authFetch(`${API}/brand-guidelines`, {  });
      if (response.ok) {
        const data = await response.json();
        setGuidelines(data);
        if (data?.input_data) {
          setFormData({
            business_name: data.input_data.business_name || "",
            industry: data.input_data.industry || "",
            target_audience: data.input_data.target_audience || "",
            brand_personality: (data.input_data.brand_personality || []).join(", "),
            core_values: (data.input_data.core_values || []).join(", "),
            unique_selling_proposition: data.input_data.unique_selling_proposition || "",
            competitors: (data.input_data.competitors || []).join(", "),
            preferred_colors: (data.input_data.preferred_colors || []).join(", "),
            existing_tagline: data.input_data.existing_tagline || ""
          });
        }
      }
    } catch (_error) {
      console.error("Error fetching guidelines:", _error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!formData.business_name || !formData.industry || !formData.target_audience) {
      toast.error("Please fill in required fields");
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        business_name: formData.business_name,
        industry: formData.industry,
        target_audience: formData.target_audience,
        brand_personality: formData.brand_personality.split(",").map(s => s.trim()).filter(Boolean),
        core_values: formData.core_values.split(",").map(s => s.trim()).filter(Boolean),
        unique_selling_proposition: formData.unique_selling_proposition,
        competitors: formData.competitors.split(",").map(s => s.trim()).filter(Boolean),
        preferred_colors: formData.preferred_colors.split(",").map(s => s.trim()).filter(Boolean),
        existing_tagline: formData.existing_tagline
      };

      const response = await authFetch(`${API}/ai/generate-brand-guidelines`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setGuidelines({ generated_guidelines: data.guidelines, input_data: payload });
        setShowForm(false);
        toast.success("Brand guidelines generated!");
      } else {
        toast.error("Failed to generate guidelines");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Handle logo file upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large (max 2MB)");
      return;
    }

    setUploadingLogo(true);
    try {
      // Convert to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const response = await authFetch(`${API}/brand-guidelines/logo/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ logo_base64: base64, logo_name: file.name })
      });

      if (response.ok) {
        toast.success("Logo uploaded successfully!");
        fetchGuidelines();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload logo");
      }
    } catch (_error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Generate AI logo
  const handleGenerateLogo = async () => {
    const businessName = guidelines?.input_data?.business_name || formData.business_name;
    const industry = guidelines?.input_data?.industry || formData.industry;
    
    if (!businessName) {
      toast.error("Please enter your business name first");
      return;
    }

    setGeneratingLogo(true);
    try {
      const response = await authFetch(`${API}/brand-guidelines/logo/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          business_name: businessName,
          industry: industry || "business",
          style: "modern minimalist professional",
          colors: guidelines?.input_data?.preferred_colors || formData.preferred_colors.split(",").map(s => s.trim()).filter(Boolean)
        })
      });

      if (response.ok) {
        toast.success("Logo generated successfully!");
        fetchGuidelines();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to generate logo");
      }
    } catch (_error) {
      toast.error("Failed to generate logo");
    } finally {
      setGeneratingLogo(false);
    }
  };

  // Delete logo
  const handleDeleteLogo = async () => {
    try {
      const response = await authFetch(`${API}/brand-guidelines/logo`, {
        method: "DELETE",
        
      });

      if (response.ok) {
        toast.success("Logo deleted");
        fetchGuidelines();
      } else {
        toast.error("Failed to delete logo");
      }
    } catch (_error) {
      toast.error("Failed to delete logo");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const g = guidelines?.generated_guidelines;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="brand-guidelines-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">Brand Guidelines</h1>
                <p className="text-sm text-neutral-500">AI-powered brand identity</p>
              </div>
            </div>
            
            {guidelines && !showForm && (
              <Button onClick={() => setShowForm(true)} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Explanation Card */}
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 mb-8">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Palette className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">AI Brand Guidelines Generator</h3>
                <p className="text-sm text-neutral-600 mb-3">
                  Create a cohesive brand identity with AI-powered guidance. Upload your logo or generate one, 
                  then let AI analyze your business to create custom color palettes, typography recommendations, 
                  brand voice guidelines, and visual style direction—all tailored to your industry and audience.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 bg-white rounded border border-amber-200 text-amber-700">Logo Upload/Generate</span>
                  <span className="px-2 py-1 bg-white rounded border border-amber-200 text-amber-700">Color Palette</span>
                  <span className="px-2 py-1 bg-white rounded border border-amber-200 text-amber-700">Typography</span>
                  <span className="px-2 py-1 bg-white rounded border border-amber-200 text-amber-700">Brand Voice</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Input Form */}
        {(!guidelines || showForm) && (
          <Card className="border-neutral-200 mb-8">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-emerald-500" />
                Generate Brand Guidelines
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Logo Section - First Step */}
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-purple-900">Step 1: Your Logo</h3>
                    <p className="text-sm text-purple-700">Upload your existing logo or generate one with AI</p>
                  </div>
                  {guidelines?.logo_url && (
                    <img src={guidelines.logo_url} alt="Logo" className="h-12 w-12 object-contain rounded border bg-white" />
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="flex-1"
                  >
                    {uploadingLogo ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Upload Logo</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleGenerateLogo}
                    disabled={generatingLogo || !formData.business_name}
                    className="flex-1"
                  >
                    {generatingLogo ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Generate with AI</>
                    )}
                  </Button>
                  {guidelines?.logo_url && (
                    <Button variant="ghost" size="sm" onClick={handleDeleteLogo} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {!formData.business_name && (
                  <p className="text-xs text-purple-600 mt-2">Enter your business name below to enable AI logo generation</p>
                )}
              </div>

              {/* Business Info */}
              <h3 className="font-medium text-gray-700 pt-2">Step 2: Business Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Business Name *</Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="Your Company Name"
                    data-testid="brand-business-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Industry *</Label>
                  <Input
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    placeholder="e.g., Technology, Healthcare, Retail"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Target Audience *</Label>
                <Textarea
                  value={formData.target_audience}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                  placeholder="Describe your ideal customers..."
                  className="min-h-16"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Brand Personality (comma-separated)</Label>
                  <Input
                    value={formData.brand_personality}
                    onChange={(e) => setFormData({ ...formData, brand_personality: e.target.value })}
                    placeholder="e.g., Professional, Innovative, Friendly"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Core Values (comma-separated)</Label>
                  <Input
                    value={formData.core_values}
                    onChange={(e) => setFormData({ ...formData, core_values: e.target.value })}
                    placeholder="e.g., Quality, Integrity, Innovation"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Unique Selling Proposition</Label>
                <Textarea
                  value={formData.unique_selling_proposition}
                  onChange={(e) => setFormData({ ...formData, unique_selling_proposition: e.target.value })}
                  placeholder="What makes your business unique?"
                  className="min-h-16"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Competitors (comma-separated)</Label>
                  <Input
                    value={formData.competitors}
                    onChange={(e) => setFormData({ ...formData, competitors: e.target.value })}
                    placeholder="e.g., Competitor A, Competitor B"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Preferred Colors (comma-separated)</Label>
                  <Input
                    value={formData.preferred_colors}
                    onChange={(e) => setFormData({ ...formData, preferred_colors: e.target.value })}
                    placeholder="e.g., Blue, Green, Gold"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Existing Tagline (if any)</Label>
                <Input
                  value={formData.existing_tagline}
                  onChange={(e) => setFormData({ ...formData, existing_tagline: e.target.value })}
                  placeholder="Your current tagline"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                {showForm && (
                  <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                )}
                <Button 
                  onClick={handleGenerate} 
                  disabled={generating}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="generate-brand-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Guidelines
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generated Guidelines */}
        {g && !g.parse_error && !showForm && (
          <div className="space-y-6">
            {/* Logo Section */}
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Image className="w-5 h-5 mr-2 text-indigo-500" />
                  Brand Logo
                </CardTitle>
              </CardHeader>
              <CardContent>
                {guidelines?.logo ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center p-6 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-200">
                      <img 
                        src={`data:image/png;base64,${guidelines.logo.logo_base64}`}
                        alt="Brand Logo"
                        className="max-w-xs max-h-48 object-contain"
                        data-testid="brand-logo-preview"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-neutral-500">
                        <Badge variant="outline" className="mr-2">
                          {guidelines.logo.logo_type === 'ai_generated' ? 'AI Generated' : 'Uploaded'}
                        </Badge>
                        {guidelines.logo.logo_name}
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingLogo}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Replace
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleGenerateLogo}
                          disabled={generatingLogo}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                        >
                          {generatingLogo ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4 mr-1" />
                          )}
                          Regenerate
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={handleDeleteLogo}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center p-8 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-300">
                      <div className="text-center">
                        <Image className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                        <p className="text-sm text-neutral-500 mb-4">No logo added yet</p>
                        <div className="flex justify-center space-x-3">
                          <Button 
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingLogo}
                            data-testid="upload-logo-btn"
                          >
                            {uploadingLogo ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            Upload Logo
                          </Button>
                          <Button 
                            onClick={handleGenerateLogo}
                            disabled={generatingLogo}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="generate-logo-btn"
                          >
                            {generatingLogo ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Sparkles className="w-4 h-4 mr-2" />
                            )}
                            Generate with AI
                          </Button>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-neutral-400 text-center">
                      Upload your existing logo (max 2MB) or let AI create one based on your brand
                    </p>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  className="hidden"
                />
              </CardContent>
            </Card>

            {/* Brand Identity */}
            {g.brand_identity && (
              <Card className="border-neutral-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <CheckCircle className="w-5 h-5 mr-2 text-emerald-500" />
                    Brand Identity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <h4 className="font-medium text-emerald-800 mb-2">Mission Statement</h4>
                      <p className="text-sm text-neutral-700">{g.brand_identity.mission_statement}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-2">Vision Statement</h4>
                      <p className="text-sm text-neutral-700">{g.brand_identity.vision_statement}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-medium text-purple-800 mb-2">Brand Promise</h4>
                    <p className="text-sm text-neutral-700">{g.brand_identity.brand_promise}</p>
                  </div>
                  <div className="p-4 bg-neutral-50 rounded-lg">
                    <h4 className="font-medium text-neutral-800 mb-2">Brand Story</h4>
                    <p className="text-sm text-neutral-700 whitespace-pre-line">{g.brand_identity.brand_story}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Visual Identity */}
            {g.visual_identity && (
              <Card className="border-neutral-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <Palette className="w-5 h-5 mr-2 text-pink-500" />
                    Visual Identity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Colors */}
                  <div>
                    <h4 className="font-medium text-neutral-800 mb-3">Primary Colors</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {g.visual_identity.primary_colors?.map((color, idx) => (
                        <ColorSwatch key={idx} color={color} />
                      ))}
                    </div>
                  </div>
                  {g.visual_identity.secondary_colors?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-neutral-800 mb-3">Secondary Colors</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {g.visual_identity.secondary_colors.map((color, idx) => (
                          <ColorSwatch key={idx} color={color} />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Typography */}
                  {g.visual_identity.typography && (
                    <div className="p-4 bg-neutral-50 rounded-lg">
                      <h4 className="font-medium text-neutral-800 mb-3 flex items-center">
                        <Type className="w-4 h-4 mr-2" />
                        Typography
                      </h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-neutral-500">Primary Font (Headlines)</p>
                          <p className="font-medium">{g.visual_identity.typography.primary_font}</p>
                        </div>
                        <div>
                          <p className="text-sm text-neutral-500">Secondary Font (Body)</p>
                          <p className="font-medium">{g.visual_identity.typography.secondary_font}</p>
                        </div>
                      </div>
                      <p className="text-sm text-neutral-600 mt-3">{g.visual_identity.typography.usage_guidelines}</p>
                    </div>
                  )}

                  {/* Logo Guidelines */}
                  {g.visual_identity.logo_guidelines && (
                    <div className="p-4 bg-neutral-50 rounded-lg">
                      <h4 className="font-medium text-neutral-800 mb-3">Logo Guidelines</h4>
                      <p className="text-sm text-neutral-700 mb-3">{g.visual_identity.logo_guidelines.concept}</p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {g.visual_identity.logo_guidelines.variations?.map((v, idx) => (
                          <Badge key={idx} variant="secondary">{v}</Badge>
                        ))}
                      </div>
                      <p className="text-sm text-neutral-600">{g.visual_identity.logo_guidelines.clear_space}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Brand Voice */}
            {g.brand_voice && (
              <Card className="border-neutral-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <MessageSquare className="w-5 h-5 mr-2 text-blue-500" />
                    Brand Voice
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {g.brand_voice.tone?.map((t, idx) => (
                      <Badge key={idx} className="bg-blue-100 text-blue-700">{t}</Badge>
                    ))}
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-2">Do&apos;s</h4>
                      <ul className="text-sm text-neutral-700 space-y-1">
                        {g.brand_voice.messaging_dos?.map((item, idx) => (
                          <li key={idx}>✓ {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                      <h4 className="font-medium text-red-800 mb-2">Don&apos;ts</h4>
                      <ul className="text-sm text-neutral-700 space-y-1">
                        {g.brand_voice.messaging_donts?.map((item, idx) => (
                          <li key={idx}>✗ {item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {g.brand_voice.sample_taglines?.length > 0 && (
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <h4 className="font-medium text-amber-800 mb-2">Suggested Taglines</h4>
                      <div className="space-y-2">
                        {g.brand_voice.sample_taglines.map((tagline, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                            <p className="text-sm font-medium">&ldquo;{tagline}&rdquo;</p>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(tagline)}>
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Application Guidelines */}
            {g.application_guidelines && (
              <Card className="border-neutral-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-lg">
                    <Layout className="w-5 h-5 mr-2 text-purple-500" />
                    Application Guidelines
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(g.application_guidelines).map(([key, value]) => (
                      <div key={key} className="p-4 bg-neutral-50 rounded-lg">
                        <h4 className="font-medium text-neutral-800 mb-2 capitalize">{key.replace(/_/g, ' ')}</h4>
                        <p className="text-sm text-neutral-600">{value}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Parse Error */}
        {g?.parse_error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-6">
              <p className="text-red-700 mb-4">There was an issue parsing the generated guidelines. Raw response:</p>
              <pre className="text-xs bg-white p-4 rounded overflow-auto max-h-96">{g.raw_response}</pre>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BrandGuidelinesPage;
